import { LANGUAGES } from './languages'

export const PermissionLevels = {
    MBA_ONLY: 'mba_only',
    EDITOR: 'editor',
    VIEWER: 'viewer',
    OWNER: 'owner',
}

export const getPermissionLevelName = (permissionLevel, defaultName = 'Unknown') => {
    switch (permissionLevel) {
        case PermissionLevels.OWNER:
            return 'Owner'
        case PermissionLevels.VIEWER:
            return 'Viewer'
        case PermissionLevels.EDITOR:
            return 'Editor'
        case PermissionLevels.MBA_ONLY:
            return 'MBA only'
        default:
            return defaultName
    }
}

export const SharingLevels = {
    NO_ACCESS: -1,
    MBA_ONLY: 1,
    VIEWABLE: 2,
    FULL_ACCESS: 4,
    OWNER: 5,
}

export const getSharingLevelName = (sharingLevel, defaultName = 'Unknown') => {
    switch (sharingLevel) {
        case SharingLevels.NO_ACCESS:
            return 'No access'
        case SharingLevels.MBA_ONLY:
            return 'MBA only'
        case SharingLevels.VIEWABLE:
            return 'Viewable'
        case SharingLevels.FULL_ACCESS:
            return 'Full access'
        case SharingLevels.OWNER:
            return 'Owner'
        default:
            return defaultName
    }
}

export const ASSIGNMENT_SHARING_LEVELS = {
    NO_ACCESS: {
        value: -1,
        label: 'No access',
        description: 'Provides no access to this assignment.',
    },
    MBA_ONLY: {
        value: 1,
        label: 'MBA only',
        description: 'Provides the ability to create MBAs from this assignment.',
    },
    VIEWABLE: {
        value: 2,
        label: 'Viewable',
        description: 'Provides the ability to view this assignment and to create MBAs from it.',
    },
    FULL_ACCESS: {
        value: 4,
        label: 'Full access',
        description: 'Provides the full access to this assignment.',
    },
    OWNER: {
        value: 5,
        label: 'Co-Owner',
        description: 'Provides owner access to this assignment.',
        languages: [LANGUAGES.NEW_TEXT.value],
    },
}

export const filterOutNoAccessLevel = sharingLevels =>
    sharingLevels.filter(level => level.value > ASSIGNMENT_SHARING_LEVELS.NO_ACCESS.value)

export const filterByLanguage = (sharingLevels, currentLanguage) => {
    return sharingLevels.filter(level => {
        if (level.languages) {
            return level.languages.includes(currentLanguage)
        }

        return true
    })
}

export const AssignmentPermissions = {
    CAN_READ: 'can-read',
    CAN_CREATE_MBA: 'can-create-mba',
    CAN_CLONE: 'can-clone',
    CAN_EXPORT: 'can-export',
    CAN_EDIT: 'can-edit',
    CAN_SHARE: 'can-share',
    CAN_USE_GPT: 'can-use-gpt',
    CAN_DELETE: 'can-delete',
    CAN_REVIEW: 'can-review',
    CAN_DISTRIBUTE_WORKLOAD: 'can-distribute-workload',
    CAN_EDIT_UNASSIGNED: 'can-edit-unassigned',
    CAN_EDIT_ASSIGNED_TO_OTHERS: 'can-edit-assigned-to-others',
}

export const ASSIGNMENT_SHARING_LEVELS_TO_PERMISSION_SCOPES = {
    [ASSIGNMENT_SHARING_LEVELS.NO_ACCESS.value]: [],
    [ASSIGNMENT_SHARING_LEVELS.MBA_ONLY.value]: [AssignmentPermissions.CAN_CREATE_MBA],
    [ASSIGNMENT_SHARING_LEVELS.VIEWABLE.value]: [
        AssignmentPermissions.CAN_READ,
        AssignmentPermissions.CAN_CREATE_MBA,
        AssignmentPermissions.CAN_REVIEW,
        AssignmentPermissions.CAN_CLONE,
        AssignmentPermissions.CAN_EXPORT,
    ],
    [ASSIGNMENT_SHARING_LEVELS.FULL_ACCESS.value]: [
        AssignmentPermissions.CAN_READ,
        AssignmentPermissions.CAN_CREATE_MBA,
        AssignmentPermissions.CAN_CLONE,
        AssignmentPermissions.CAN_EXPORT,
        AssignmentPermissions.CAN_EDIT,
        AssignmentPermissions.CAN_SHARE,
        AssignmentPermissions.CAN_REVIEW,
    ],
    [ASSIGNMENT_SHARING_LEVELS.OWNER.value]: [
        AssignmentPermissions.CAN_READ,
        AssignmentPermissions.CAN_CREATE_MBA,
        AssignmentPermissions.CAN_CLONE,
        AssignmentPermissions.CAN_EXPORT,
        AssignmentPermissions.CAN_EDIT,
        AssignmentPermissions.CAN_SHARE,
        AssignmentPermissions.CAN_REVIEW,
        AssignmentPermissions.CAN_DISTRIBUTE_WORKLOAD,
        AssignmentPermissions.CAN_EDIT_UNASSIGNED,
        AssignmentPermissions.CAN_EDIT_ASSIGNED_TO_OTHERS,
    ],
}

export const PERMISSION_LEVEL_TO_PERMISSION_SCOPES = {
    [PermissionLevels.MBA_ONLY]: [AssignmentPermissions.CAN_CREATE_MBA],
    [PermissionLevels.VIEWER]: [
        AssignmentPermissions.CAN_READ,
        AssignmentPermissions.CAN_CREATE_MBA,
        AssignmentPermissions.CAN_REVIEW,
        AssignmentPermissions.CAN_CLONE,
        AssignmentPermissions.CAN_EXPORT,
    ],
    [PermissionLevels.EDITOR]: [
        AssignmentPermissions.CAN_READ,
        AssignmentPermissions.CAN_CREATE_MBA,
        AssignmentPermissions.CAN_CLONE,
        AssignmentPermissions.CAN_EXPORT,
        AssignmentPermissions.CAN_EDIT,
        AssignmentPermissions.CAN_SHARE,
        AssignmentPermissions.CAN_REVIEW,
    ],
    [PermissionLevels.OWNER]: [
        AssignmentPermissions.CAN_READ,
        AssignmentPermissions.CAN_CREATE_MBA,
        AssignmentPermissions.CAN_CLONE,
        AssignmentPermissions.CAN_EXPORT,
        AssignmentPermissions.CAN_EDIT,
        AssignmentPermissions.CAN_SHARE,
        AssignmentPermissions.CAN_DELETE,
        AssignmentPermissions.CAN_REVIEW,
        AssignmentPermissions.CAN_DISTRIBUTE_WORKLOAD,
        AssignmentPermissions.CAN_EDIT_UNASSIGNED,
        AssignmentPermissions.CAN_EDIT_ASSIGNED_TO_OTHERS,
    ],
}

export const Forbiddances = {
    IS_IN_CONFIRM_CHANGES_MODE: 'is-in-confirm-changes-mode',
    IS_MBA: 'is-mba',
    VERSION_HAS_MBAS: 'version-has-mbas',
    IS_VERSION_NOT_LATEST: 'is-version-not-latest',
    IS_VERSION_PUBLISHED: 'is-version-published',
    IS_PROCESSING: 'is-processing',
}
